<template>
  <div class="form">
    <OurGroups>
      <OurGroup :title="$t('dealershipApplications.modalDesc')">
        <div v-for="(email, index) in form.emails" :key="email.id" class="flex items-start">
          <OurInput
            v-model="email.email"
            :label="$t('label.email')"
            :placeholder="$t('placeholder.email')"
            :error="emailError(index)"
            @focus="letsEditEmail(email.id)"
            @blur="closeEdit"
          />
          <div v-if="email.id" class="flex">
            <OurButton
              v-if="getPermission['notice.notifications']"
              class="settings-btn ms-3"
              :text="$t('button.edit')"
              variant="secondary"
              @click="updateEmail(email.id, email.email)"
            >
              <OurSvgIcon :src="icons.Edit" size="sm" />
            </OurButton>
            <OurButton
              v-if="getPermission['notice.notifications']"
              class="settings-btn ms-3"
              :text="$t('button.delete')"
              variant="secondary"
              @click="deleteEmail(email.id)"
            >
              <OurSvgIcon :src="icons.Trash" size="sm" />
            </OurButton>
          </div>
          <OurButton
            v-else
            class="settings-btn ms-4"
            :text="$t('button.save')"
            @click="createEmail(email.email)"
          />
        </div>
        <OurButton
          v-if="getPermission['notice.notifications'] && hasId"
          class="w-full"
          :text="$t('button.addEmail')"
          variant="secondary"
          @click="addEmail"
        />
      </OurGroup>
      <OurGroup class="!mt-8" :title="$t('dealershipApplications.managerNorm')">
        <OurInput
          v-model="localManagerProcessingNormHours"
          only-digits
          :min="0"
          :max="999"
          :label="$t('dealershipApplications.managerNormHours')"
          :placeholder="$t('placeholder.managerNormHours')"
          :error="managerProcessingNormHoursError"
        />
        <OurInput
          v-model="localManagerProcessingNormMinutes"
          only-digits
          :min="0"
          :max="59"
          :label="$t('dealershipApplications.managerNormMinutes')"
          :placeholder="$t('placeholder.managerNormMinutes')"
          :error="managerProcessingNormMinutesError"
        />
        <OurButton
          v-if="getPermission['notice.notifications']"
          class="w-full"
          :text="$t('button.save')"
          variant="secondary"
          @click="saveManagerProcessingNorm"
        />
      </OurGroup>
    </OurGroups>
  </div>
</template>

<script>
import { email, integer, maxValue, minValue, required } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";

export default {
  name: "SettingsForm",

  props: {
    value: {
      type: Object,
      default: () => {},
    },
    managerProcessingNormHours: {
      type: Number,
      default: 0,
    },
    managerProcessingNormMinutes: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      form: {
        emails: [],
      },
      edited: null,
      localManagerProcessingNormHours: 0,
      localManagerProcessingNormMinutes: 0,
    };
  },

  validations: {
    form: {
      emails: {
        $each: {
          email: { required, email },
        },
      },
    },
    localManagerProcessingNormHours: {
      required,
      integer,
      minValue: minValue(0),
      maxValue: maxValue(999),
    },
    localManagerProcessingNormMinutes: {
      required,
      integer,
      minValue: minValue(0),
      maxValue: maxValue(59),
    },
  },

  computed: {
    ...mapGetters("user", ["getPermission"]),
    icons: () => ({
      Trash: require("@/components/_bit/IconSvg/icons/Trash.svg"),
      Edit: require("@/components/_bit/IconSvg/icons/Edit.svg"),
    }),
    hasId() {
      return this.form.emails.every((email) => email.id);
    },

    managerProcessingNormHoursError() {
      const field = this.$v.localManagerProcessingNormHours;

      if (!field.$dirty) {
        return "";
      }

      if (!field.required || !field.integer || !field.minValue || !field.maxValue) {
        return this.$t("validation.managerProcessingNormHours");
      }

      return "";
    },

    managerProcessingNormMinutesError() {
      const field = this.$v.localManagerProcessingNormMinutes;

      if (!field.$dirty) {
        return "";
      }

      if (!field.required || !field.integer || !field.minValue || !field.maxValue) {
        return this.$t("validation.managerProcessingNormMinutes");
      }

      return "";
    },
  },

  watch: {
    value: {
      handler: "setForm",
      deep: true,
      immediate: true,
    },

    form: {
      handler: "onChangeForm",
      deep: true,
    },

    managerProcessingNormHours: {
      handler(value) {
        this.localManagerProcessingNormHours = value ?? 0;
      },
      immediate: true,
    },

    managerProcessingNormMinutes: {
      handler(value) {
        this.localManagerProcessingNormMinutes = value ?? 0;
      },
      immediate: true,
    },
  },

  methods: {
    emailError(index) {
      const isDirty = this.$v.form.emails.$each[index].email.$dirty;
      const isFilled = this.$v.form.emails.$each[index].email.required;
      const isEmail = this.$v.form.emails.$each[index].email.email;

      let error = "";

      if (isDirty && !isFilled) {
        error = this.$t("validation.required");
      } else if (isDirty && !isEmail) {
        error = this.$t("validation.mustBeEmail");
      }

      return error;
    },

    setForm() {
      this.form = this.value;
    },

    onChangeForm() {
      this.$emit("input", this.form);
    },

    letsEditEmail(id) {
      this.edited = id;
    },

    closeEdit() {
      this.edited = null;
    },

    createEmail(email) {
      this.$emit("create", email);
    },

    updateEmail(id, email) {
      this.$emit("update", { id, email });
    },

    deleteEmail(id) {
      this.$emit("delete", id);
    },

    addEmail() {
      this.form.emails.push({ email: "" });
    },

    saveManagerProcessingNorm() {
      this.$v.localManagerProcessingNormHours.$touch();
      this.$v.localManagerProcessingNormMinutes.$touch();

      if (
        this.$v.localManagerProcessingNormHours.$invalid ||
        this.$v.localManagerProcessingNormMinutes.$invalid
      ) {
        return;
      }

      this.$emit("save-manager-processing-norm", {
        hours: Number(this.localManagerProcessingNormHours),
        minutes: Number(this.localManagerProcessingNormMinutes),
      });
    },

    emitForm() {
      this.$v.form.$touch();

      if (this.$v.form.$invalid) {
        this.form.isValid = false;

        return;
      }

      this.form.isValid = true;
      this.$emit("input", this.form);
    },

    resetFormState() {
      this.localManagerProcessingNormHours = this.managerProcessingNormHours ?? 0;
      this.localManagerProcessingNormMinutes = this.managerProcessingNormMinutes ?? 0;
      this.edited = null;
      this.$v.form.$reset();
      this.$v.localManagerProcessingNormHours.$reset();
      this.$v.localManagerProcessingNormMinutes.$reset();
    },
  },
};
</script>

<style scoped>
.settings-btn {
  height: 57px;
}
</style>
